import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import { ShieldCheck } from "lucide-react";
import StatusModal from "../../../components/Modal/StatusModal";
import { organizationService } from "../../../services/api/organization";
import Button2 from "../../../components/elements/forms/Button2";
import DPAContent from "./DPAContent";
import PdfPreviewModal from "../../../components/Modal/PdfPreviewModal";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { documentService } from "../../../services/api/documentService";

const DPAPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const organizationId = searchParams.get("Id") || "";
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [uploadedSignature, setUploadedSignature] = useState<File | null>(null);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [previewModal, setPreviewModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const contentRef = React.useRef<HTMLDivElement>(null);

  const [orgData, setOrgData] = useState({
    name: "[Company Name]",
    representativeName: "[Representative Name]",
    representativeRole: "[Representative Role]",
  });

  const today = format(new Date(), "do MMMM, yyyy");

  useEffect(() => {
    const fetchOrgData = async () => {
      if (!organizationId) {
        toast.error("Organization ID not found");
        setLoading(false);
        return;
      }

      try {
        const response = await organizationService.get(organizationId);
        if (response?.data) {
          setOrgData({
            name: response.data.name || "[Company Name]",
            representativeName:
              response.data.representativeName || "[Representative Name]",
            representativeRole:
              response.data.representativeRole || "[Representative Role]",
          });
        }
      } catch (error) {
        console.error("Error fetching organization:", error);
        toast.error("Failed to load organization details");
      } finally {
        setLoading(false);
      }
    };

    fetchOrgData();
  }, [organizationId]);

  const handleFileUpload = (file: File) => {
    setUploadedSignature(file);
    const previewUrl = URL.createObjectURL(file);
    setSignaturePreview(previewUrl);
  };

  const handleRemoveSignature = () => {
    if (signaturePreview) {
      URL.revokeObjectURL(signaturePreview);
    }
    setUploadedSignature(null);
    setSignaturePreview(null);
  };

  const generatePDF = async (): Promise<Blob | null> => {
    if (!contentRef.current) return null;
    try {
      const canvas = await html2canvas(contentRef.current, {
        scale: 1.5,
        useCORS: true,
        logging: false,
      });
      const imgData = canvas.toDataURL("image/jpeg", 0.75);
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });
      const imgWidth = 210;
      const pageHeight = 297;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(
        imgData,
        "JPEG",
        0,
        position,
        imgWidth,
        imgHeight,
        undefined,
        "FAST",
      );
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(
          imgData,
          "JPEG",
          0,
          position,
          imgWidth,
          imgHeight,
          undefined,
          "FAST",
        );
        heightLeft -= pageHeight;
      }
      return pdf.output("blob");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF document");
      return null;
    }
  };

  const handlePreview = async () => {
    if (!isAgreed) {
      toast.error("Please agree to the Data Processing Addendum to continue.");
      return;
    }
    // if (!uploadedSignature) {
    //   toast.error("Please upload your signature before submitting.");
    //   return;
    // }
    if (!organizationId) {
      toast.error("Organization ID is missing.");
      return;
    }

    setPreviewModal(true);
    setPdfBlobUrl(null);
    setPdfFile(null);

    try {
      const pdfBlob = await generatePDF();
      if (!pdfBlob) {
        setPreviewModal(false);
        return;
      }
      const file = new File(
        [pdfBlob],
        `DPA_${orgData.name}_${format(new Date(), "yyyy-MM-dd")}.pdf`,
        { type: "application/pdf" },
      );
      setPdfFile(file);
      setPdfBlobUrl(URL.createObjectURL(pdfBlob));
    } catch (error) {
      console.error("Error generating PDF preview:", error);
      toast.error("Failed to generate PDF preview.");
      setPreviewModal(false);
    }
  };

  const handleSubmit = async () => {
    if (!pdfFile || !organizationId) return;
    setSubmitting(true);
    try {
      await documentService.uploadDocument(organizationId, "DPA", pdfFile);

      setPreviewModal(false);
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
      setPdfFile(null);

      setShowStatusModal(true);
    } catch (error: any) {
      console.error("Error submitting DPA:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit DPA";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleClosePreview = () => {
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    setPdfBlobUrl(null);
    setPdfFile(null);
    setPreviewModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10">
        <div className="max-w-[1000px] mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10  rounded-lg flex items-center justify-center">
              <ShieldCheck size={24} className="text-primary" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-900 leading-none">
                Data Processing Addendum
              </h1>
              <p className="text-xs text-gray-500 mt-1">25th Suite Policies</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-[1000px] mx-auto px-4 py-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div ref={contentRef}>
            <DPAContent
              clientName={orgData.name}
              representativeName={orgData.representativeName}
              representativeRole={orgData.representativeRole}
              date={today}
              effectiveDate={today}
              readOnly={false}
              uploadedSignature={uploadedSignature}
              signaturePreview={signaturePreview}
              onFileUpload={handleFileUpload}
              onRemoveSignature={handleRemoveSignature}
            />
          </div>
        </div>

        {/* Action Section */}
        <div className="mt-8 bg-white px-6 py-4 rounded-xl border border-gray-200 shadow-sm flex items-center justify-between sticky bottom-4">
          <div className="flex items-start gap-3">
            <input
              type="checkbox"
              id="agreement"
              checked={isAgreed}
              onChange={(e) => setIsAgreed(e.target.checked)}
              className="mt-1 w-5 h-5 cursor-pointer accent-primary"
            />
            <label
              htmlFor="agreement"
              className="text-sm font-medium text-gray-700 leading-relaxed cursor-pointer"
            >
              I confirm that I have read and agree to the Data Processing
              Addendum.
            </label>
          </div>

          <div className="flex justify-end gap-3">
            {/* <button
              onClick={() => navigate(-1)}
              className="px-6 py-2 border border-gray-300 rounded-md hover:bg-gray-50 text-gray-700 font-medium transition-colors"
            >
              Back
            </button> */}
            <Button2
              title="Preview DPA"
              onSubmitHandler={handlePreview}
              loading={submitting}
              className={`px-8 transition-colors ${
                isAgreed
                  ? "bg-primary hover:bg-primary/90 text-white"
                  : "bg-gray-400 cursor-not-allowed"
              }`}
              disabled={!isAgreed}
            />
          </div>
        </div>
      </div>

      <PdfPreviewModal
        isOpen={previewModal}
        onClose={handleClosePreview}
        pdfUrl={pdfBlobUrl}
        onSubmit={handleSubmit}
        isSubmitting={submitting}
        title="Data Processing Addendum Preview"
        submitButtonText="Submit DPA"
      />

      <StatusModal
        isOpen={showStatusModal}
        status="success"
        message="Your Data Processing Addendum has been submitted."
        onClose={() => setShowStatusModal(false)}
        onAction={() => {
          navigate({
            pathname: "#",
            search: "Check your email for next steps",
            // search: searchParams.toString(),
          });
        }}
      />
    </div>
  );
};

export default DPAPage;
